import { MessageSquare, Medal, ChevronDown } from 'lucide-react';

import { MessageBodyMarkdown } from '@/components/messages/message-body-markdown';
import { getAvatarColorClasses } from '@/lib/messages/avatar-color';
import { initialsFromLabel } from '@/lib/messages/messages-helpers';
import { cn } from '@/lib/utils';

/**
 * A miniature of the patient-app doctor profile, driven straight off the editor's form state.
 *
 * Deliberately renders the PATIENT app's light palette as literal values rather than this app's
 * theme tokens: it is a simulation of another application's screen, so it must look identical
 * whether the staff app is in light or dark mode. Those literals are the design's own tokens
 * (healo_app `ui_kit/app_colors.dart` light) — the one place in healo_msg where that is correct.
 *
 * Non-interactive on purpose: no scroll-nav fade, no CTA wiring. The About block renders in its
 * COLLAPSED state so authors can see where the fold lands.
 */

const INK = '#0F172A';
const INK_2 = '#475569';
const INK_3 = '#94A3B8';
const BORDER = '#E2E6F0';
const PRIMARY = '#3B82F6';
const PRIMARY_DARK = '#2563EB';
const PRIMARY_SOFT = '#EDF3FE';
const ELEVATED = '#F1F3F9';

/** Design frame: the patient screen is authored at 390x844 and shown at 288x624. */
const DEVICE_WIDTH = 390;
const DEVICE_HEIGHT = 844;
const PREVIEW_SCALE = 0.7385;

/** "Dr Jason Lee" → bold "Dr Jason" over light "Lee". */
function splitNameLockup({ firstName, lastName, displayName }) {
  const first = String(firstName || '').trim();
  const last = String(lastName || '').trim();
  if (first || last) return { lead: `Dr ${first}`.trim(), tail: last };
  const raw = String(displayName || '').trim().replace(/^dr\.?\s+/i, '');
  if (!raw) return { lead: 'Doctor', tail: '' };
  const parts = raw.split(/\s+/);
  if (parts.length === 1) return { lead: `Dr ${parts[0]}`, tail: '' };
  return { lead: `Dr ${parts.slice(0, -1).join(' ')}`, tail: parts[parts.length - 1] };
}

export function DoctorProfilePreview({
  firstName,
  lastName,
  displayName,
  photo,
  headline,
  specialties = [],
  qualifications = [],
  clinicalInterests = [],
  languages = [],
  bio = '',
  quote = '',
  awards = [],
  seed = '',
}) {
  const { lead, tail } = splitNameLockup({ firstName, lastName, displayName });
  const fullName = [lead, tail].filter(Boolean).join(' ');
  const cleanAwards = awards.filter((a) => String(a?.title || '').trim());

  // `zoom` rather than `transform: scale` — a transform does not change the layout box, so the
  // scroll container would see the content at full size and scroll far too far. zoom shrinks the
  // box itself, which is what makes the frame scrollable at the right extent.
  const scaled = {
    width: DEVICE_WIDTH,
    zoom: PREVIEW_SCALE,
    fontFamily: 'Lato, -apple-system, system-ui, sans-serif',
    color: INK,
  };

  return (
    <div
      // Fills the space the dialog can spare, capped at the design height. A fixed height would
      // be squeezed by the surrounding flex column, leaving a stub of a phone.
      className="relative min-h-0 w-full flex-1 overflow-hidden rounded-[34px] shadow-[0_18px_40px_-18px_rgba(15,23,42,0.5)] ring-1 ring-black/10"
      style={{
        width: DEVICE_WIDTH * PREVIEW_SCALE,
        maxHeight: DEVICE_HEIGHT * PREVIEW_SCALE,
        background: '#FFFFFF',
      }}
    >
      {/* Scrolls so an author can check the fold — the profile is taller than one screen. */}
      <div
        className="doctor-preview-scroll"
        style={{ position: 'absolute', inset: 0, overflowY: 'auto', overflowX: 'hidden' }}
      >
        <div style={scaled}>
        {/* Hero */}
        <div
          style={{
            position: 'relative',
            height: 392,
            background: 'linear-gradient(168deg,#E7EFFC 0%,#EDF2FB 46%,#F7F8FC 100%)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              width: 340,
              height: 340,
              right: -80,
              top: -40,
              borderRadius: '50%',
              background:
                'radial-gradient(circle at 50% 50%,rgba(59,130,246,.16),rgba(59,130,246,0) 68%)',
            }}
          />
          {photo ? (
            <img
              src={photo}
              alt=""
              style={{
                position: 'absolute',
                right: -14,
                bottom: 0,
                height: 330,
                zIndex: 2,
                filter: 'drop-shadow(0 18px 26px rgba(15,23,42,.16))',
              }}
            />
          ) : (
            // No portrait: an initials disc where the cutout would be — never a stretched avatar.
            <div
              className={cn(
                'grid place-items-center rounded-full text-[34px] font-bold',
                getAvatarColorClasses(seed || fullName),
              )}
              style={{ position: 'absolute', right: 34, bottom: 54, width: 96, height: 96, zIndex: 2 }}
            >
              {initialsFromLabel(fullName)}
            </div>
          )}
          <div style={{ position: 'absolute', left: 26, bottom: 38, zIndex: 3, width: photo ? 186 : 240 }}>
            {headline ? (
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 900,
                  letterSpacing: 1.3,
                  textTransform: 'uppercase',
                  color: PRIMARY,
                  lineHeight: 1.5,
                  marginBottom: 12,
                }}
              >
                {headline}
              </div>
            ) : null}
            <div style={{ fontSize: 33, fontWeight: 900, letterSpacing: -1.1, lineHeight: 1.02 }}>
              {lead}
              {tail ? (
                <span
                  style={{ display: 'block', fontWeight: 300, fontSize: 31, letterSpacing: -0.8 }}
                >
                  {tail}
                </span>
              ) : null}
            </div>
            {/* No role line: it repeated the headline eyebrow and the specialty chip. */}
          </div>
        </div>

        {/* Sheet */}
        <div
          style={{
            position: 'relative',
            zIndex: 3,
            marginTop: -30,
            background: '#FFFFFF',
            borderRadius: '28px 28px 0 0',
            padding: '26px 24px 132px',
            boxShadow: '0 -14px 30px -18px rgba(15,23,42,.22)',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 11,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 38,
              height: 4,
              borderRadius: 2,
              background: BORDER,
            }}
          />

          {specialties.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {specialties.map((s) => (
                <span
                  key={s}
                  style={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    color: PRIMARY_DARK,
                    background: PRIMARY_SOFT,
                    padding: '7px 13px',
                    borderRadius: 999,
                  }}
                >
                  {s}
                </span>
              ))}
            </div>
          ) : null}

          {qualifications.length > 0 ? (
            <div
              style={{
                marginTop: 20,
                padding: '16px 0',
                borderTop: `1px solid ${BORDER}`,
                borderBottom: `1px solid ${BORDER}`,
              }}
            >
              <div
                style={{
                  fontSize: 9.5,
                  fontWeight: 900,
                  letterSpacing: 1.4,
                  textTransform: 'uppercase',
                  color: INK_3,
                }}
              >
                Qualifications
              </div>
              <div style={{ marginTop: 9, display: 'flex', flexWrap: 'wrap' }}>
                {qualifications.map((q, i) => (
                  <span key={q} style={{ fontSize: 13.5, lineHeight: 1.5, whiteSpace: 'nowrap' }}>
                    {q}
                    {i < qualifications.length - 1 ? (
                      <span style={{ color: INK_3, padding: '0 7px' }}>·</span>
                    ) : null}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {(clinicalInterests.length > 0 || languages.length > 0) ? (
            <div style={{ marginTop: 22, display: 'grid', gap: 16 }}>
              {clinicalInterests.length > 0 ? (
                <Fact label="Clinical interests" value={clinicalInterests.join(' · ')} />
              ) : null}
              {languages.length > 0 ? (
                <Fact label="Speaks" value={languages.join(', ')} italic />
              ) : null}
            </div>
          ) : null}

          {bio.trim() ? (
            <>
              <SheetHeading>About</SheetHeading>
              <div style={{ position: 'relative' }}>
                <div
                  className="doctor-preview-bio"
                  style={{
                    maxHeight: 250,
                    overflow: 'hidden',
                    WebkitMaskImage: 'linear-gradient(180deg,#000 62%,transparent 100%)',
                    maskImage: 'linear-gradient(180deg,#000 62%,transparent 100%)',
                  }}
                >
                  <MessageBodyMarkdown>{bio}</MessageBodyMarkdown>
                </div>
                <div
                  style={{
                    marginTop: 6,
                    fontSize: 13.5,
                    fontWeight: 700,
                    color: PRIMARY,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 0',
                  }}
                >
                  Read full profile
                  <ChevronDown size={13} strokeWidth={2.6} />
                </div>
              </div>
            </>
          ) : null}

          {quote.trim() ? (
            <div
              style={{
                marginTop: 24,
                padding: 20,
                borderRadius: 20,
                background: 'linear-gradient(160deg,#F2F7FE,#F7F8FC)',
              }}
            >
              <p style={{ fontSize: 15, lineHeight: 1.6, fontStyle: 'italic' }}>“{quote.trim()}”</p>
              <div
                style={{
                  marginTop: 10,
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing: 0.9,
                  textTransform: 'uppercase',
                  color: INK_3,
                }}
              >
                {fullName}
              </div>
            </div>
          ) : null}

          {cleanAwards.length > 0 ? (
            <>
              <SheetHeading>Recognition</SheetHeading>
              <div>
                {cleanAwards.map((award, i) => (
                  <div
                    key={`${award.title}-${i}`}
                    style={{
                      display: 'flex',
                      gap: 14,
                      alignItems: 'flex-start',
                      padding: '14px 0',
                      borderBottom: i < cleanAwards.length - 1 ? `1px solid ${BORDER}` : 'none',
                    }}
                  >
                    <span
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        background: ELEVATED,
                        display: 'grid',
                        placeItems: 'center',
                        flex: 'none',
                        marginTop: 1,
                        color: INK_2,
                      }}
                    >
                      <Medal size={15} strokeWidth={1.9} />
                    </span>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.35 }}>
                        {award.title}
                      </div>
                      {award.note ? (
                        <div style={{ fontSize: 12, color: INK_3, marginTop: 3 }}>{award.note}</div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>

        </div>
      </div>

      {/* Pinned to the FRAME, not the scroller — in the app this bar never scrolls away.
          The z-index is load-bearing: the sheet inside the scroller sits at z-index 3, so without
          one the scrolling content paints straight over this bar. */}
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 35 }}>
        <div style={scaled}>
          <div
            style={{
              padding: '14px 20px 26px',
              background:
                'linear-gradient(180deg,rgba(255,255,255,0),rgba(255,255,255,.94) 32%,#fff 60%)',
              display: 'flex',
              gap: 10,
            }}
          >
            <div
              style={{
                width: 52,
                height: 52,
                borderRadius: 16,
                background: ELEVATED,
                display: 'grid',
                placeItems: 'center',
                color: INK,
                flex: 'none',
              }}
            >
              <MessageSquare size={20} strokeWidth={2} />
            </div>
            <div
              style={{
                flex: 1,
                height: 52,
                borderRadius: 16,
                background: PRIMARY,
                color: '#fff',
                fontSize: 15.5,
                fontWeight: 700,
                display: 'grid',
                placeItems: 'center',
                boxShadow: '0 10px 22px -10px rgba(59,130,246,.85)',
              }}
            >
              Book with {tail || lead}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SheetHeading({ children }) {
  return (
    <h2 style={{ fontSize: 19, fontWeight: 900, letterSpacing: -0.5, margin: '30px 0 12px' }}>
      {children}
    </h2>
  );
}

function Fact({ label, value, italic }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '104px 1fr', gap: 12, alignItems: 'baseline' }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 900,
          letterSpacing: 0.8,
          textTransform: 'uppercase',
          color: INK_3,
          lineHeight: 1.5,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 14,
          lineHeight: 1.55,
          color: italic ? INK_2 : INK,
          fontStyle: italic ? 'italic' : 'normal',
        }}
      >
        {value}
      </div>
    </div>
  );
}
